import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Heart, 
  MessageCircle, 
  ShoppingCart, 
  Instagram, 
  Facebook, 
  Mail, 
  ChevronDown, 
  ChevronUp, 
  Palette, 
  Award,
  User,
  Menu,
  X,
  ExternalLink,
  Camera,
  Sparkles,
  Plus,
  Eye,
  Send,
  Loader,
  Calendar
} from 'lucide-react';
import Footer from './Footer';
import './HomePage.css';
import Exhibition from './Exhibition';
import SEO from './SEO';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
const API_URL ='https://my-musing-backend.onrender.com/api';

const HomePage = () => {
  const [artworks, setArtworks] = useState([]);
  const [displayedArtworks, setDisplayedArtworks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState(['All']);
  const [artistProfile, setArtistProfile] = useState({});
  const [achievements, setAchievements] = useState([]);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
 const [scrollPosition, setScrollPosition] = useState(null);
  const navigate = useNavigate();
  const [commentForm, setCommentForm] = useState({ name: '', comment: '' });
  const [buyForm, setBuyForm] = useState({
    name: '', email: '', phone: '', whatsapp: '', address: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', whatsapp: '', message: ''
  });
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [itemsToShow, setItemsToShow] = useState(10);
  const [touchActiveCards, setTouchActiveCards] = useState(new Set());
   const [currentPage, setCurrentPage] = useState(1);
   const [carouselIndex, setCarouselIndex] = useState(0);
const [carouselItemsPerView, setCarouselItemsPerView] = useState(5);
  const itemsPerPage = useMemo(() => ({
    mobile: 6,
    tablet: 10,
    desktop: 10
  }), []);
   const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
    touchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    orientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  });

  const getItemsPerPage = useCallback(() => {
    if (viewport.isMobile) return itemsPerPage.mobile;
    if (viewport.isTablet) return itemsPerPage.tablet;
    return itemsPerPage.desktop;
  }, [viewport.isMobile, viewport.isTablet, itemsPerPage]);
 const filteredArtworks = useMemo(() => {
    if (selectedGenres.includes('All') || selectedGenres.length === 0) {
      return artworks;
    }
    return artworks.filter(artwork =>
      artwork.genres.some(genre => selectedGenres.includes(genre))
    );
  }, [artworks, selectedGenres]);
  
  useEffect(() => {
    let resizeTimeout;
    
    const updateViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setViewport({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        touchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        orientation: height > width ? 'portrait' : 'landscape'
      });
      
      if (width >= 1024 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateViewport, 150);
    };
    
    const handleOrientationChange = () => {
      setTimeout(() => {
        updateViewport();
        setMobileMenuOpen(false);
      }, 200);
    };

    updateViewport();
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true });
    
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && 
          !event.target.closest('.main-nav') && 
          !event.target.closest('.mobile-menu-btn')) {
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

useEffect(() => {
  const preventScroll = showCommentModal || showCommentsModal || 
                       showBuyModal || showContactModal || showImageModal;
  
  if (preventScroll) {
    // Save current scroll position
    const scrollY = window.scrollY;
    setScrollPosition(scrollY);
    
    // Lock body scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  } else if (scrollPosition !== null) {
    // Restore body scroll
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    
    // Restore scroll position after a small delay
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  }
}, [showCommentModal, showCommentsModal, showBuyModal, showContactModal, showImageModal]);

  useEffect(() => {
    fetchData();
  }, []);
useEffect(() => {
  const updateCarouselItems = () => {
    const width = window.innerWidth;
    if (width <= 768) {
      setCarouselItemsPerView(1); // 1 item on mobile
    } else if (width <= 1024) {
      setCarouselItemsPerView(4); // 4 items on tablet
    } else {
      setCarouselItemsPerView(5); // 5 items on desktop
    }
  };

  updateCarouselItems();
  window.addEventListener('resize', updateCarouselItems);
  return () => window.removeEventListener('resize', updateCarouselItems);
}, []);

// 3. REPLACE ALL CAROUSEL HANDLER FUNCTIONS with these:

const handleCarouselPrev = useCallback(() => {
  setCarouselIndex(prev => Math.max(0, prev - 1));
}, []);

const handleCarouselNext = useCallback(() => {
  const imageAchievements = achievements.filter(a => a.imageUrl !== "");
  const maxIndex = Math.max(0, imageAchievements.length - carouselItemsPerView);
  
  setCarouselIndex(prev => Math.min(maxIndex, prev + 1));
}, [achievements, carouselItemsPerView]);

const handleIndicatorClick = useCallback((index) => {
  const imageAchievements = achievements.filter(a => a.imageUrl !== "");
  const maxIndex = Math.max(0, imageAchievements.length - carouselItemsPerView);
  
  setCarouselIndex(Math.min(index, maxIndex));
}, [achievements, carouselItemsPerView]);

// 4. REPLACE getCarouselTransform with this FIXED VERSION:
const getCarouselTransform = useCallback(() => {
  const imageAchievements = achievements.filter(a => a.imageUrl !== "");
  if (imageAchievements.length === 0) return 'translateX(0)';
  
  // Desktop/Tablet: show multiple items
  if (carouselItemsPerView > 1) {
    // Calculate card width as percentage
    const cardWidthPercent = 100 / carouselItemsPerView;
    
    // Gap in percentage (approximation)
    const gapPercent = (20 / (viewport.width - 140)) * 100; // 20px gap, 140px padding
    
    // Total width per item including gap
    const itemWidth = cardWidthPercent + gapPercent;
    
    // Calculate offset
    const offset = carouselIndex * itemWidth;
    
    return `translateX(-${Math.min(offset, (imageAchievements.length - carouselItemsPerView) * itemWidth)}%)`;
  } 
  // Mobile: show 1 item at a time
  else {
    // Each item is 100% + gap
    const gapPercent = (15 / (viewport.width - 120)) * 100; // 15px gap on mobile
    const itemWidth = 100 + gapPercent;
    
    const offset = carouselIndex * itemWidth;
    
    return `translateX(-${Math.min(offset, (imageAchievements.length - 1) * itemWidth)}%)`;
  }
}, [carouselIndex, carouselItemsPerView, achievements, viewport.width]);

// 5. ADD this useEffect to reset index on viewport change:
useEffect(() => {
  // Reset to first item when items per view changes
  setCarouselIndex(0);
}, [carouselItemsPerView]);

// 6. KEEP/ADD the swipe gesture support:
useEffect(() => {
  if (!viewport.touchDevice) return;
  
  const carouselWrapper = document.querySelector('.carousel-wrapper');
  if (!carouselWrapper) return;
  
  let touchStartX = 0;
  let touchEndX = 0;
  let isSwiping = false;
  
  const handleTouchStart = (e) => {
    touchStartX = e.changedTouches[0].screenX;
    isSwiping = true;
  };
  
  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    touchEndX = e.changedTouches[0].screenX;
  };
  
  const handleTouchEnd = () => {
    if (!isSwiping) return;
    isSwiping = false;
    
    const swipeThreshold = 50;
    const diff = touchStartX - touchEndX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        handleCarouselNext();
      } else {
        handleCarouselPrev();
      }
    }
    
    touchStartX = 0;
    touchEndX = 0;
  };
  
  carouselWrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
  carouselWrapper.addEventListener('touchmove', handleTouchMove, { passive: true });
  carouselWrapper.addEventListener('touchend', handleTouchEnd, { passive: true });
  
  return () => {
    carouselWrapper.removeEventListener('touchstart', handleTouchStart);
    carouselWrapper.removeEventListener('touchmove', handleTouchMove);
    carouselWrapper.removeEventListener('touchend', handleTouchEnd);
  };
}, [viewport.touchDevice, handleCarouselNext, handleCarouselPrev]);

// 7. KEEP/ADD keyboard navigation:
useEffect(() => {
  const handleKeyDown = (e) => {
    const carouselContainer = document.querySelector('.carousel-container');
    if (!carouselContainer) return;
    
    const rect = carouselContainer.getBoundingClientRect();
    const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
    
    if (isInViewport) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleCarouselPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleCarouselNext();
      }
    }
  };
  
  document.addEventListener('keydown', handleKeyDown);
  return () => document.removeEventListener('keydown', handleKeyDown);
}, [handleCarouselPrev, handleCarouselNext]);
  useEffect(() => {
    filterArtworks();
  }, [selectedGenres, artworks, itemsToShow]);

  const fetchData = async () => {
    try {
      const [artworksRes, genresRes, profileRes, achievementsRes] = await Promise.all([
        axios.get(`${API_URL}/artworks`),
        axios.get(`${API_URL}/genres`),
        axios.get(`${API_URL}/artist-profile`),
        axios.get(`${API_URL}/achievements`)
      ]);

      setArtworks(artworksRes.data);
      setGenres(['All', ...genresRes.data]);
      setArtistProfile(profileRes.data);
      setAchievements(achievementsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterArtworks = () => {
    let filtered = artworks;
    
    if (!selectedGenres.includes('All') && selectedGenres.length > 0) {
      filtered = artworks.filter(artwork =>
        artwork.genres.some(genre => selectedGenres.includes(genre))
      );
    }
    
    setDisplayedArtworks(filtered.slice(0, itemsToShow));
  };

  const handleGenreClick = (genre) => {
    if (genre === 'All') {
      setSelectedGenres(['All']);
    } else {
      let newSelected = selectedGenres.filter(g => g !== 'All');
      if (selectedGenres.includes(genre)) {
        newSelected = newSelected.filter(g => g !== genre);
        if (newSelected.length === 0) {
          newSelected = ['All'];
        }
      } else {
        newSelected.push(genre);
      }
      setSelectedGenres(newSelected);
    }
    
    const resetItems = viewport.isMobile ? 4 : viewport.isTablet ? 6 : 10;
    setItemsToShow(resetItems);
  };

  const handleShowMore = () => {
    setShowMoreLoading(true);
    setTimeout(() => {
      const increment = viewport.isMobile ? 4 : viewport.isTablet ? 4 : 10;
      setItemsToShow(prev => prev + increment);
      setShowMoreLoading(false);
    }, 500);
  };
 const handleBrandClick = () =>{

 }
  const handleLike = async (artworkId) => {
    try {
      await axios.post(`${API_URL}/artworks/${artworkId}/like`);
      setArtworks(prev => prev.map(artwork =>
        artwork.id === artworkId
          ? { ...artwork, likes: (artwork.likes || 0) + 1 }
          : artwork
      ));
    } catch (error) {
      console.error('Error liking artwork:', error);
    }
  };

  const handleTouchStart = useCallback((artworkId, type = 'artwork') => {
    if (viewport.touchDevice) {
      setTouchActiveCards(prev => new Set([...prev, `${type}-${artworkId}`]));
    }
  }, [viewport.touchDevice]);

  const handleTouchEnd = useCallback((artworkId, type = 'artwork') => {
    if (viewport.touchDevice) {
      setTimeout(() => {
        setTouchActiveCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(`${type}-${artworkId}`);
          return newSet;
        });
      }, 2000);
    }
  }, [viewport.touchDevice]);

  const openImageModal = (artwork) => {
    setSelectedArtwork(artwork);
    setShowImageModal(true);
  };

  const openCommentsModal = (artwork) => {
    setSelectedArtwork(artwork);
    setShowCommentsModal(true);
  };

  const closeModal = (modalType) => {
    switch (modalType) {
      case 'image':
        setShowImageModal(false);
        break;
      case 'comment':
        setShowCommentModal(false);
        break;
      case 'comments':
        setShowCommentsModal(false);
        break;
      case 'buy':
        setShowBuyModal(false);
        break;
      case 'contact':
        setShowContactModal(false);
        break;
    }
    setSelectedArtwork(null);
  };

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  let date;
  
  // Handle MongoDB/Mongoose timestamp formats
  if (timestamp.seconds) {
    // Firestore timestamp
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp._seconds) {
    // Alternative Firestore format
    date = new Date(timestamp._seconds * 1000);
  } else if (timestamp instanceof Date) {
    // Already a Date object
    date = timestamp;
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    // ISO string or Unix timestamp
    date = new Date(timestamp);
  } else {
    // Default: try to convert
    date = new Date(timestamp);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentForm.name.trim() || !commentForm.comment.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/artworks/${selectedArtwork.id}/comment`, commentForm);
      setCommentForm({ name: '', comment: '' });
      setShowCommentModal(false);
      
      const response = await axios.get(`${API_URL}/artworks`);
      setArtworks(response.data);
      alert('Comment added successfully!');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment. Please try again.');
    }
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!buyForm.name.trim() || !buyForm.email.trim() || !buyForm.phone.trim() || !buyForm.address.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/buy-request`, {
        artworkId: selectedArtwork.id,
        buyerDetails: buyForm
      });
      setBuyForm({ name: '', email: '', phone: '', whatsapp: '', address: '' });
      setShowBuyModal(false);
      alert('Your purchase request has been sent! We will contact you soon.');
    } catch (error) {
      console.error('Error sending buy request:', error);
      alert('Error sending request. Please try again.');
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.phone.trim() || !contactForm.message.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/contact`, contactForm);
      setContactForm({ name: '', email: '', phone: '', whatsapp: '', message: '' });
      setShowContactModal(false);
      alert('Your message has been sent! We will get back to you soon.');
    } catch (error) {
      console.error('Error sending contact message:', error);
      alert('Error sending message. Please try again.');
    }
  };

  const getFilteredArtworksCount = () => {
    if (selectedGenres.includes('All') || selectedGenres.length === 0) {
      return artworks.length;
    }
    return artworks.filter(artwork =>
      artwork.genres.some(genre => selectedGenres.includes(genre))
    ).length;
  };

  const smoothScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      const headerHeight = viewport.isMobile ? 60 : viewport.isTablet ? 70 : 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      if (mobileMenuOpen) {
        setTimeout(() => setMobileMenuOpen(false), 300);
      }
    }
  };


  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => {
      const newState = !prev;
      
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(30);
      }
      
      if (newState) {
        setTimeout(() => {
          const firstNavItem = document.querySelector('.main-nav .nav-link');
          if (firstNavItem) {
            firstNavItem.focus();
          }
        }, 300);
      } else {
        setTimeout(() => {
          const menuBtn = document.querySelector('.mobile-menu-btn');
          if (menuBtn) {
            menuBtn.focus();
          }
        }, 100);
      }
      
      return newState;
    });
  };
  

  const handleNavItemClick = (action) => {
    if (typeof action === 'function') {
      action();
    } else if (typeof action === 'string') {
      smoothScrollTo(action);
    }
    
    if (mobileMenuOpen && !viewport.isDesktop) {
      setTimeout(() => setMobileMenuOpen(false), 200);
    }
  };

const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Debraj Chatterjee",
  "alternateName": ["Debraj Musing", "My Musing"],
  "url": "https://debrajmusing.com",  
  "image": artistProfile.photo || "https://debrajmusing.com/logo.png",
  "jobTitle": "Professional Fine Artist",
  "description": artistProfile.bio || "Professional fine artist specializing in portraits, landscapes, and contemporary art",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Baidyabati, Hooghly",
    "addressRegion": "West Bengal",
    "addressCountry": "IN"
  },
  "knowsAbout": ["Portrait Art", "Landscape Art", "Contemporary Art", "Fine Art", "Abstract Art"],
  "sameAs": [
    artistProfile.instagram,
    artistProfile.facebook
  ].filter(Boolean)
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "My Musing - Art and Creations",
  "alternateName": "Debraj Musing",
  "url": "https://debrajmusing.com",
  "description": "Explore stunning art portfolio by Debraj Chatterjee. Professional fine artist specializing in portraits, landscapes, and contemporary art.",
  "author": {
    "@type": "Person",
    "name": "Debraj Chatterjee"
  }
};
 

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <Loader className="loading-spinner" size={48} />
          <h3>Loading Gallery...</h3>
          <p>Preparing beautiful artworks for you</p>
        </div>
      </div>
    );
  }

  return (
    
    <div className="premium-portfolio">
       <Helmet>
  <title>My Musing - Art and Creations by Debraj Chatterjee | Debraj Musing</title>
  <meta name="description" content="Explore stunning art portfolio by Debraj Chatterjee (Debraj Musing). Professional fine artist from Baidyabati, Hooghly, West Bengal specializing in portraits, landscapes, and contemporary art. Commission custom artwork." />
  
  <script type="application/ld+json">
    {JSON.stringify(personSchema)}
  </script>
  <script type="application/ld+json">
    {JSON.stringify(websiteSchema)}
  </script>
</Helmet>
      <header className="modern-header">
        <div className="header-container">
          <div className="header-content">
            <div className="brand" onClick={() =>handleNavItemClick('artworks')}>
              <img src="/logo.png" alt="My Musing Logo" className="brand-logo" />
              <div className="brand-text">
                <h1>MY MUSING</h1>
                <p>ART AND CREATIONS</p>
              </div>
            </div>

            <nav className={`main-nav ${mobileMenuOpen ? 'nav-open' : ''}`} id="main-nav">
              <button
                onClick={() => handleNavItemClick('artworks')} 
                className="nav-link"
                type="button"
                aria-label="Go to Gallery section"
              >
                <Palette size={18} />
                <span>Gallery</span>
              </button>
              
              <button 
                onClick={() => handleNavItemClick('artist')} 
                className="nav-link"
                type="button"
                aria-label="Go to Artist section"
              >
                <User size={18} />
                <span>Artist</span>
              </button>
              <button 
  onClick={() => handleNavItemClick('exhibitions')} 
  className="nav-link"
  type="button"
  aria-label="Go to Exhibitions section"
>
  <Calendar size={18} />
  <span>Exhibitions</span>
</button>
              <button 
                onClick={() => handleNavItemClick('achievements')} 
                className="nav-link"
                type="button"
                aria-label="Go to Achievements section"
              >
                <Award size={18} />
                <span>Achievements</span>
              </button>
<button 
  onClick={() => navigate('/blog')} 
  className="nav-link"
  type="button"
  aria-label="Go to Blog section"
>
  <MessageCircle size={18} />
  <span>Blog</span>
</button>
              <button 
                onClick={() => handleNavItemClick(() => setShowContactModal(true))} 
                className="nav-link contact-btn"
                type="button"
                aria-label="Open contact form"
              >
                <Mail size={18} />
                <span>Contact Us</span>
              </button>
              
              <a 
                href="/artist-login" 
                className="nav-link login-btn"
                aria-label="Go to Artist Portal"
                onClick={() => mobileMenuOpen && setMobileMenuOpen(false)}
              >
                <User size={18} />
                <span>Artist Portal</span>
              </a>
            </nav>

            <button 
              className="mobile-menu-btn"
              onClick={toggleMobileMenu}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="main-nav"
              type="button"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <section id="artworks" className="filter-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              Artistic Collections
              <span className="title-accent">({getFilteredArtworksCount()} works)</span>
            </h2>
            <p className="section-subtitle">
              Explore diverse artistic expressions across multiple genres and styles
            </p>
          </div>
          
          <div className="genre-filter-container">
            <div className="genre-grid">
              {genres.map(genre => (
                <button
                  key={genre}
                  className={`genre-pill ${selectedGenres.includes(genre) ? 'active' : ''}`}
                  onClick={() => handleGenreClick(genre)}
                  aria-pressed={selectedGenres.includes(genre)}
                  type="button"
                >
                  <span>{genre}</span>
                  {selectedGenres.includes(genre) && genre !== 'All' && (
                    <X 
                      size={14} 
                      className="remove-icon" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenreClick(genre);
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="gallery-section">
        <div className="section-container">
          <div className="artwork-grid">
            {displayedArtworks.map((artwork, index) => (
              <article 
                key={artwork.id} 
                className={`artwork-card ${touchActiveCards.has(`artwork-${artwork.id}`) ? 'touch-active' : ''}`}
                style={{
                  animationDelay: `${(index % (viewport.isMobile ? 2 : viewport.isTablet ? 3 : 4)) * 0.1}s`
                }}
                onTouchStart={() => handleTouchStart(artwork.id, 'artwork')}
                onTouchEnd={() => handleTouchEnd(artwork.id, 'artwork')}
              >
                <div 
                  className="artwork-image-wrapper" 
                  onClick={() => openImageModal(artwork)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${artwork.title} in detail`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openImageModal(artwork);
                    }
                  }}
                >
                  <img 
                    src={artwork.imageUrl} 
                    alt={artwork.title} 
                    className="artwork-image"
                    loading={index < (viewport.isMobile ? 2 : 4) ? 'eager' : 'lazy'}
                  />
                  
                  <div className="artwork-overlay">
                    <div className="overlay-top">
                      <div>
                        <h3 className="overlay-title">{artwork.title}</h3>
                        <p className="overlay-description">{artwork.description}</p>
                      </div>
                    </div>
                    
                    <div className="overlay-bottom">
                      <div className="overlay-stats">
                        <div className="stat-item" onClick={(e) => {
                            e.stopPropagation();
                            handleLike(artwork.id);
                          }}
                          aria-label="Like this artwork"
                          style={{ cursor: 'pointer' }}
                          type="button">
                          <Heart size={16} fill={artwork.likes > 0 ? '#ff6b6b' : 'none'} />
                          <span>{artwork.likes || 0}</span>
                        </div>
                        <div 
                          className="stat-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCommentsModal(artwork);
                          }}
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openCommentsModal(artwork);
                            }
                          }}
                        >
                          <MessageCircle size={16} />
                          <span>{artwork.comments?.length || 0}</span>
                        </div>
                      </div>
                      
                      <div className="overlay-actions">
                        <button
                          className="overlay-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLike(artwork.id);
                          }}
                          aria-label="Like this artwork"
                          type="button"
                        >
                          <Heart size={15} />
                          {!viewport.isMobile && <span>Like</span>}
                        </button>
                        <button
                          className="overlay-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedArtwork(artwork);
                            setShowCommentModal(true);
                          }}
                          aria-label="Comment on this artwork"
                          type="button"
                        >
                          <MessageCircle size={15} />
                          {/* {!viewport.isMobile && <span>Comment</span>} */}
                        </button>
                        <button
                          className="overlay-action primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedArtwork(artwork);
                            setShowBuyModal(true);
                          }}
                          aria-label="Purchase this artwork"
                          type="button"
                        >
                          <ShoppingCart size={15} />
                          {/* {!viewport.isMobile && <span>Buy</span>} */}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {displayedArtworks.length < getFilteredArtworksCount() && (
            <div className="show-more-container">
              <button 
                className="show-more-btn"
                onClick={handleShowMore}
                disabled={showMoreLoading}
                // aria-label={`Load more artworks. ${getFilteredArtworksCount() - displayedArtworks.length} ning`}
                type="button"
              >
                {showMoreLoading ? (
                  <>
                    <Loader size={20} />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    <span>Show More</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </section>

      <section id="artist" className="artist-section">
        <div className="section-container">
          <div className="artist-profile-card">
            <div className="artist-visual">
              <div className="artist-image-container">
                {artistProfile.photo ? (
                  <img 
                    src={artistProfile.photo} 
                    alt={artistProfile.name || 'Artist'} 
                    className="artist-photo" 
                    loading="lazy"
                  />
                ) : (
                  <div className="artist-placeholder">
                    <Camera size={viewport.isMobile ? 24 : 32} />
                  </div>
                )}
                <div className="artist-badge">
                  <Sparkles size={viewport.isMobile ? 16 : 20} />
                </div>
              </div>
            </div>
            
            <div className="artist-details">
              <div className="artist-header">
                <h2 className="artist-name">
                  {artistProfile.name || 'Debraj Chatterjee'}
                </h2>
                <p className="artist-title">
                  {artistProfile.designation || 'Professional Fine Artist'}
                </p>
              </div>
              
              <div className="artist-bio">
                <p>
                  {artistProfile.bio || 'A passionate artist dedicated to creating captivating visual experiences through various mediums. Specializing in portraits, landscapes, and contemporary art that speaks to the human experience.'}
                </p>
              </div>
              
              <div className="artist-social">
                {artistProfile.instagram && (
                  <a 
                    href={artistProfile.instagram} 
                    className="social-link instagram"
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Visit Instagram profile"
                  >
                    <Instagram size={20} />
                    <span>Instagram</span>
                    <ExternalLink size={16} />
                  </a>
                )}
                {artistProfile.facebook && (
                  <a 
                    href={artistProfile.facebook} 
                    className="social-link facebook"
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Visit Facebook profile"
                  >
                    <Facebook size={20} />
                    <span>Facebook</span>
                    <ExternalLink size={16} />
                  </a>
                )}
                <button 
                  onClick={() => setShowContactModal(true)} 
                  className="social-link contact"
                  aria-label="Open contact form"
                  type="button"
                >
                  <Mail size={20} />
                  <span>Get in Touch</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
<Exhibition />
<section id="achievements" className="achievements-section">
  <div className="section-container">
    <div className="section-header">
      <h2 className="section-title">
        Achievements & Recognition
      </h2>
      <p className="section-subtitle">
        Celebrating artistic milestones and professional accomplishments
      </p>
    </div>
    
    {/* Text-Only Achievements */}
    {achievements.filter(a => a.imageUrl === "").length > 0 && (
      <div className="text-achievements-container">
        <div className="text-achievements-header">
        </div>
        <div className="text-achievements-grid">
          {achievements.filter(a => a.imageUrl === "").map((achievement, index) => (
            <article 
              key={achievement.id} 
              className="text-achievement-card"
              style={{
                animationDelay: `${index * 0.05}s`
              }}
            >
              <div className="text-achievement-content">
                <div className="text-achievement-icon">
                  <Award size={24} />
                </div>
                <div className="text-achievement-details">
                  <h3 className="text-achievement-title">{achievement.title}</h3>
                  {achievement.description && (
                    <p className="text-achievement-description">{achievement.description}</p>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    )}

    {/* Image-Based Achievements - Carousel */}
    {achievements.filter(a => a.imageUrl !== "").length > 0 && (
      <div className="carousel-achievements-container">
        <div className="carousel-achievements-header">
          <h3>Featured Achievements</h3>
          <p>Documented milestones with visual memories</p>
        </div>

        <div className="carousel-container">
          <button 
            className={`carousel-btn carousel-btn-left ${carouselIndex === 0 ? 'disabled' : ''}`}
            onClick={handleCarouselPrev}
            disabled={carouselIndex === 0}
            aria-label="Previous achievements"
            type="button"
          >
            ‹
          </button>
          
          <div className="carousel-wrapper">
            <div 
              className="carousel-track"
              style={{ transform: getCarouselTransform() }}
            >
              {achievements.filter(a => a.imageUrl !== "").map((achievement, index) => (
                <article
                  key={achievement.id}
                  className={`carousel-achievement-card ${touchActiveCards.has(`carousel-${achievement.id}`) ? 'touch-active' : ''}`}
                  onTouchStart={() => handleTouchStart(achievement.id, 'carousel')}
                  onTouchEnd={() => handleTouchEnd(achievement.id, 'carousel')}
                >
                  <img 
                    src={achievement.imageUrl} 
                    alt={achievement.title}
                    loading="lazy"
                  />
                  <div className="carousel-achievement-caption">
                    <h2>{achievement.title}</h2>
                     <h3>{achievement.description}</h3>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <button 
            className={`carousel-btn carousel-btn-right ${
              carouselIndex >= achievements.filter(a => a.imageUrl !== "").length - carouselItemsPerView ? 'disabled' : ''
            }`}
            onClick={handleCarouselNext}
            disabled={carouselIndex >= achievements.filter(a => a.imageUrl !== "").length - carouselItemsPerView}
            aria-label="Next achievements"
            type="button"
          >
            ›
          </button>
        </div>

        <div className="carousel-indicators">
          {Array.from({ 
            length: Math.ceil(achievements.filter(a => a.imageUrl !== "").length / carouselItemsPerView) 
          }).map((_, index) => (
            <button
              key={index}
              className={`carousel-indicator ${
                Math.floor(carouselIndex / carouselItemsPerView) === index ? 'active' : ''
              }`}
              onClick={() => handleIndicatorClick(index)}
              aria-label={`Go to page ${index + 1}`}
              type="button"
            />
          ))}
        </div>
      </div>
    )}

    {achievements.length === 0 && (
      <div className="no-achievements">
        <Award size={64} />
        <p>No achievements added yet</p>
      </div>
    )}
  </div>
</section>
      {showContactModal && (
        <div className="modal-overlay" onClick={() => closeModal('contact')}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={() => closeModal('contact')}
              type="button"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>Commission Custom Art</h3>
              <p>Share your vision and let's create something beautiful together</p>
            </div>
            
            <form onSubmit={handleContact} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    className='text-input'
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Your full name"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    className='text-input'
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    required
                    className='text-input'
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp</label>
                  <input
                    type="tel"
                    className='text-input'
                    value={contactForm.whatsapp}
                    onChange={(e) => setContactForm({ ...contactForm, whatsapp: e.target.value })}
                    placeholder="WhatsApp number (optional)"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Your Vision *</label>
                <textarea
                  required
                  rows="4"
                  className='text-input'
                  placeholder="Describe your commission idea, preferred style, size, timeline, and any specific requirements..."
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                />
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <Send size={18} />
                  <span>Send Message</span>
                </button>
                <button 
                  type="button" 
                  onClick={() => closeModal('contact')} 
                  className="btn-secondary"
                >
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCommentModal && selectedArtwork && (
        <div className="modal-overlay" onClick={() => closeModal('comment')}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => closeModal('comment')} type="button">
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>Add Comment</h3>
              <p>Share your thoughts about this artwork</p>
            </div>
            
            <div className="modal-artwork-preview">
              <img src={selectedArtwork.imageUrl} alt={selectedArtwork.title} />
              <div className="preview-info">
                <h4>{selectedArtwork.title}</h4>
                <p>{selectedArtwork.description}</p>
              </div>
            </div>
            
            <form onSubmit={handleComment} className="modal-form">
              <div className="form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  required
                  value={commentForm.name}
                  className='text-input'
                  onChange={(e) => setCommentForm({ ...commentForm, name: e.target.value })}
                  placeholder="Enter your name"
                />
              </div>
              
              <div className="form-group">
                <label>Your Comment *</label>
                <textarea
                  required
                  rows="4"
                  className='text-input'
                  placeholder="Share your thoughts about this artwork..."
                  value={commentForm.comment}
                  onChange={(e) => setCommentForm({ ...commentForm, comment: e.target.value })}
                />
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <Send size={18} />
                  <span>Post Comment</span>
                </button>
                <button type="button" onClick={() => closeModal('comment')} className="btn-secondary">
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCommentsModal && selectedArtwork && (
        <div className="modal-overlay" onClick={() => closeModal('comments')}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => closeModal('comments')} type="button">
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>Comments ({selectedArtwork.comments?.length || 0})</h3>
              <p>{selectedArtwork.title}</p>
            </div>
            
            <div className="modal-body">
             {selectedArtwork.comments && selectedArtwork.comments.length > 0 ? (
  <div className="comments-list">
    {selectedArtwork.comments.map((comment, index) => (
      <div key={index} className="comment-item">
        <div className="comment-avatar">
          {comment.name.toUpperCase()}
        </div>
        <div className="comment-content">
          <div>
            {/* <span className="comment-author">{comment.name}</span> */}
            <span className="comment-text">{comment.comment}</span>
          </div>
          <span className="comment-date">{formatDate(comment.createdAt)}</span>
        </div>
      </div>
    ))}
  </div>
) : (
  <div className="no-comments">
    <MessageCircle size={48} className="no-svg" />
    <p className="no-p">No comments yet. Be the first to share your thoughts!</p>
  </div>
)}
            </div>
            
            <div className="modal-form" style={{ paddingTop: 0 }}>
              <div className="modal-actions">
                <button 
                  onClick={(e) => {
                    closeModal('comments');
                    e.stopPropagation();
                    setSelectedArtwork(selectedArtwork);
                    setShowCommentModal(true);
                  }}
                  className="btn-primary"
                  type="button"
                >
                  <MessageCircle size={18} />
                  <span>Add Comment</span>
                </button>
                <button type="button" onClick={() => closeModal('comments')} className="btn-secondary">
                  <span>Close</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBuyModal && selectedArtwork && (
        <div className="modal-overlay" onClick={() => closeModal('buy')}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => closeModal('buy')} type="button">
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>Purchase Artwork</h3>
              <p>Complete your information to request this artwork</p>
            </div>
            
            <div className="modal-artwork-preview">
              <img src={selectedArtwork.imageUrl} alt={selectedArtwork.title} />
              <div className="preview-info">
                <h4>{selectedArtwork.title}</h4>
                <p>{selectedArtwork.description}</p>
                <div className="preview-genres">
                  {selectedArtwork.genres?.map(genre => (
                    <span key={genre} className="genre-tag">{genre}</span>
                  ))}
                </div>
              </div>
            </div>
            
            <form onSubmit={handleBuy} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    className='text-input'
                    type="text"
                    required
                    value={buyForm.name}
                    onChange={(e) => setBuyForm({ ...buyForm, name: e.target.value })}
                    placeholder="Your full name"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    className='text-input'
                    type="email"
                    required
                    value={buyForm.email}
                    onChange={(e) => setBuyForm({ ...buyForm, email: e.target.value })}
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    className='text-input'
                    type="tel"
                    required
                    value={buyForm.phone}
                    onChange={(e) => setBuyForm({ ...buyForm, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp</label>
                  <input
                    className='text-input'
                    type="tel"
                    value={buyForm.whatsapp}
                    onChange={(e) => setBuyForm({ ...buyForm, whatsapp: e.target.value })}
                    placeholder="WhatsApp number (optional)"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Delivery Address *</label>
                <textarea
                  className='text-input'
                  required
                  rows="3"
                  placeholder="Enter your complete delivery address..."
                  value={buyForm.address}
                  onChange={(e) => setBuyForm({ ...buyForm, address: e.target.value })}
                />
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <ShoppingCart size={18} />
                  <span>Submit Purchase Request</span>
                </button>
                <button type="button" onClick={() => closeModal('buy')} className="btn-secondary">
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImageModal && selectedArtwork && (
        <div className="modal-overlay" onClick={() => closeModal('image')} style={{ padding: 0 }}>
          <button 
            className="modal-close" 
            onClick={() => closeModal('image')} 
            type="button"
            style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1100 }}
          >
            <X size={24} />
          </button>
          <img 
            src={selectedArtwork.imageUrl} 
            alt={selectedArtwork.title}
            style={{
              maxWidth: '95vw',
              maxHeight: '95vh',
              objectFit: 'contain',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
      <Footer 
        artistProfile={artistProfile}
        onContactClick={() => setShowContactModal(true)}
        onSectionClick={smoothScrollTo}
      />

    </div>
  );
};

export default HomePage;